import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { followHandoff } from '@/auth/handoff'
import { DEFAULT_HOME, NEXT_PARAM, withNext } from '@/auth/routes'
import { useBrand } from '@/brand/context'
import { useTenants } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { FullPageLoading } from '@/ui/states'
import { PublicPage } from './PublicPage'
import { useToken } from './useToken'

/** The one refusal the person reading it can act on. */
const ADDRESS_MISMATCH = 'INVITATION_ADDRESS_MISMATCH'

/**
 * The query parameter `/signup` reads the invitation token from. A signup that
 * carries it creates the account and accepts the invitation in one request
 * (`POST /auth/signup` with `invitation_token`), so the colleague never has to
 * open a workspace of their own to join somebody else's.
 */
export const INVITATION_PARAM = 'invitation'

/**
 * `/accept-invitation` — the link `invitation_link()` builds.
 *
 * Public in the routing sense — the guard does not send it to `/login` — but
 * accepting needs a session, because a membership needs a user. So the page has
 * a state the other three do not: **signed out**, which offers sign-in
 * carrying `?next=` back to this exact URL, token and all, and sign-up
 * carrying the token itself (`?invitation=`) so the account is created and the
 * invitation accepted together. Losing the token on the way through sign-in
 * is the failure mode that makes an invitation unredeemable, and it is why
 * `next` is built from `location` rather than from a bare path.
 *
 * **On the apex, and that is where the emailed link points.** An
 * organisation's own host signs in only its members and signs nobody up, and
 * the person holding an invitation is by definition not a member yet. So the
 * link lands here on `augrank.com`; accepting binds the session to the
 * organisation and the API answers `handoff_url` — the organisation's host —
 * which is followed as a full navigation, the way a sign-in's is. Accepted on
 * the organisation's own host (a signed-in member with a second invitation),
 * there is nothing to hand off to and the page says they are in.
 *
 * Redeemed on a button press, never on load. It is a POST, and it is single-use.
 *
 * Every refusal the API can give is shown, and only one of them is actionable:
 * `INVITATION_ADDRESS_MISMATCH` names the fix, because an invitation is
 * addressed to an *email address* and the signed-in account is not it. Expired,
 * revoked and already-used all return one `INVITATION_INVALID` on purpose —
 * telling them apart tells anyone holding a token which guess was closest.
 */
export function AcceptInvitation(): JSX.Element {
  const brand = useBrand()
  const token = useToken()
  const location = useLocation()
  const navigate = useNavigate()
  const { status, user, refresh } = useAuth()
  const { adopt } = useTenants()
  const [joined, setJoined] = useState<string | null>(null)
  // The browser has been told to go to the organisation's host. Held as state
  // so the button does not come back to life for the frame between the
  // navigation being asked for and the page unloading.
  const [leaving, setLeaving] = useState(false)

  const accept = useMutation((body: { token: string }) =>
    call(() => api.POST('/api/v1/invitations/accept', { body })),
  )

  const here = `${location.pathname}${location.search}`

  if (status === 'loading') return <FullPageLoading />
  if (leaving) return <FullPageLoading label="Opening your workspace" />

  if (!token) {
    return (
      <PublicPage title="That invitation link is incomplete">
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            The address in your browser has no invitation code in it. Ask whoever invited you to
            send it again — links expire after seven days, so a fresh one is best anyway.
          </p>
        </div>
      </PublicPage>
    )
  }

  if (status === 'anonymous') {
    return (
      <PublicPage
        title={`You have been invited to ${brand.productName}`}
        footer="Your invitation is held until you sign in — this link keeps working."
      >
        <div className="ar-card__body ar-stack">
          <p style={{ marginTop: 0 }}>
            Sign in to join. If you have never used {brand.productName}, create an account with{' '}
            <strong>the address the invitation was sent to</strong> — an invitation is addressed to
            a mailbox, not to a person, and a different address cannot redeem it.
          </p>
          <Link className="ar-btn ar-btn--primary ar-btn--block" to={withNext('/login', here)}>
            Sign in
          </Link>
          <Link
            className="ar-btn ar-btn--ghost ar-btn--block"
            to={`/signup?${INVITATION_PARAM}=${encodeURIComponent(token)}&${NEXT_PARAM}=${encodeURIComponent(here)}`}
          >
            Create an account
          </Link>
        </div>
      </PublicPage>
    )
  }

  if (joined) {
    return (
      <PublicPage title={`You are in ${joined}`}>
        <div className="ar-card__body ar-stack">
          <p className="ar-badge ar-badge--success" style={{ marginTop: 0 }}>
            <span aria-hidden="true">✓ </span>Membership added
          </p>
          <p className="ar-muted">
            {joined} is now the organisation you are working in. You can switch at any time from the
            sidebar.
          </p>
          <Button
            tone="primary"
            onClick={() => {
              void navigate(DEFAULT_HOME, { replace: true })
            }}
          >
            Go to {brand.productName}
          </Button>
        </div>
      </PublicPage>
    )
  }

  const failure = accept.state.failure
  const mismatch = failure?.code === ADDRESS_MISMATCH

  return (
    <PublicPage title="Join this organisation">
      <div className="ar-card__body ar-stack">
        <InlineError failure={failure} />
        {mismatch ? (
          <p className="ar-muted" style={{ marginTop: 0 }}>
            You are signed in as <strong>{user?.email ?? user?.mobile ?? 'this account'}</strong>.
            Sign out and sign in with the address the invitation was sent to, or ask for a new
            invitation to this address.
          </p>
        ) : (
          <p style={{ marginTop: 0 }}>
            Accepting adds your account to the organisation and makes it the one you are working in.
            You will be signed in as{' '}
            <strong>{user?.email ?? user?.mobile ?? 'your current account'}</strong>.
          </p>
        )}
        <Button
          tone="primary"
          busy={accept.pending}
          busyLabel="Joining"
          onClick={() => {
            void (async () => {
              const result = await accept.run({ token })
              if (!result.ok) return
              // On the apex the organisation's host is where the work is, and
              // the API has said so: a full navigation, before anything here
              // is adopted — the page is about to unload.
              if (followHandoff(result.data.handoff_url)) {
                setLeaving(true)
                return
              }
              // Accepting joins the organisation **and selects it on the
              // session**, so both the list and the session have moved. Adopt
              // the one and re-read the other before offering the way in.
              adopt({ tenant: result.data.tenant, membership: result.data.membership })
              await refresh()
              setJoined(result.data.tenant.name)
            })()
          }}
        >
          {failure ? 'Try again' : 'Accept invitation'}
        </Button>
      </div>
    </PublicPage>
  )
}
